import React from "react";
import "./TypewriterText.css";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Hard ceiling on the whole reveal, so long copy never crawls. */
const MAX_MS = 700;
/**
 * Milliseconds between state updates.
 *
 * Deliberately not per-frame. An earlier version advanced one character per
 * requestAnimationFrame, which meant ~60 setState calls per second per
 * instance; with a couple of dozen typewriters on a page that saturated the
 * main thread and locked the tab. Now every instance does a fixed ~22 updates
 * total regardless of string length, revealing a chunk at a time.
 */
const TICK_MS = 32;

type TypewriterTextProps = {
  text: string;
  /** Show a caret while typing. Off for body copy, on for headings. */
  caret?: boolean;
  className?: string;
};

/**
 * Types text out one character at a time.
 *
 * The film's own mechanism — the page filling with a repeated line — applied
 * to arbitrary strings. Speed scales with length so a paragraph finishes in
 * the same beat as a title rather than taking ten seconds.
 *
 * Width is reserved up front by an invisible copy of the full string, so
 * typing never reflows the layout around it.
 */
const TypewriterText: React.FC<TypewriterTextProps> = ({
  text,
  caret = false,
  className = "",
}) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const reduce = prefersReducedMotion();
  const [count, setCount] = React.useState(() => (reduce ? text.length : 0));
  const [typing, setTyping] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || reduce) return;

    let timer = 0;
    let failsafe = 0;

    const run = () => {
      setTyping(true);
      const ticks = Math.max(1, Math.round(MAX_MS / TICK_MS));
      const chunk = Math.max(1, Math.ceil(text.length / ticks));
      let shown = 0;

      timer = window.setInterval(() => {
        shown = Math.min(text.length, shown + chunk);
        setCount(shown);
        if (shown >= text.length) {
          window.clearInterval(timer);
          setTyping(false);
        }
      }, TICK_MS);
    };

    // Text must never stay hidden if the observer misses under StrictMode's
    // mount/unmount/remount.
    failsafe = window.setTimeout(() => setCount(text.length), 1200);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          window.clearTimeout(failsafe);
          run();
          observer.disconnect();
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => {
      window.clearTimeout(failsafe);
      window.clearInterval(timer);
      observer.disconnect();
    };
  }, [text, reduce]);

  return (
    <span ref={ref} className={`typed ${className}`.trim()} aria-label={text}>
      <span aria-hidden="true">{text.slice(0, count)}</span>
      {caret && typing && <span className="typed__caret" aria-hidden="true" />}
      {/*
        The untyped remainder, held invisible rather than removed, so it
        reserves the final width and nothing reflows as the line fills.

        visibility is set inline as well as in CSS on purpose. The class alone
        would leave a one-frame flash of the whole string if the stylesheet
        had not applied yet — harmless in production, where CSS is extracted
        into the head before first paint, but real in dev where it is injected
        by JS. Inline cannot lose that race.
      */}
      <span
        className="typed__rest"
        style={{ visibility: "hidden" }}
        aria-hidden="true"
      >
        {text.slice(count)}
      </span>
    </span>
  );
};

export default TypewriterText;
